import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Select from '@mui/material/Select'
import CircularProgress from '@mui/material/CircularProgress'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import { useQueryClient } from '@tanstack/react-query'

import { useSendTuyaDevices, useGetTuyaDevices, useCreateTuyaConnection } from '@/hooks/use-tuya'
import { FormControlWithLabelWrapper } from '../../../views/wizard/steps/wizard-common'
import CustomButton from '@/components/custom-button'

export default function TuyaDevicesWrapper({
    applianceId,
    setShowGeneration,
}: {
    applianceId: number,
    setShowGeneration: (boolean) => void,
}) {
    
    const { status, data, error } = useSendTuyaDevices()

    if (status === 'pending') {
        return <Box sx={{
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        }}>
            <CircularProgress />
        </Box>
    }

    if (error) {
        return (
            <Box>
                <ErrorOutlineRoundedIcon />
                <Box>{error.message}</Box>
            </Box>
        )
    }

    return (
        <TuyaDevices key={data.trid} trid={data.trid} applianceId={applianceId} setShowGeneration={setShowGeneration} />
    )
}

function TuyaDevices({
    trid,
    applianceId,
    setShowGeneration,
}: {
    trid: number,
    applianceId: number,
    setShowGeneration: (boolean) => void,
}) {

    const [keepRefetching, setKeepRefetching] = useState<boolean>(true)
    const [socketId, setSocketId] = useState<string>('')

    const [requestInProgress, setRequestInProgress] = useState<boolean>(false)
    const [connectionSuccess, setConnectionSuccess] = useState<string | null>(null)
    const [connectionError, setConnectionError] = useState<string | null>(null)

    const [waiting, setWaiting] = useState(true)

    useEffect(() => {
        const timer = setTimeout(() => {
            setWaiting(false)
        }, 20000)
        return () => clearTimeout(timer)
    }, [])

    const mutation = useCreateTuyaConnection(
        applianceId,
        setConnectionSuccess,
        setConnectionError,
        setRequestInProgress,
    )

    const handleConnect = () => {
        setRequestInProgress(true)
        mutation.mutate(socketId)
    }

    const queryClient = useQueryClient()

    const handleRefetch = () => {
        queryClient.invalidateQueries({ queryKey: ['connectedTuyaApp'] })
        queryClient.invalidateQueries({ queryKey: ['sendTuyaDevices'] })
    }
    
    const { status, data, error } = useGetTuyaDevices(trid, keepRefetching)

    useEffect(() => {
        if (data?.status === 'completed') {
            setKeepRefetching(false)
            setWaiting(false)
        }
    }, [data])

    if (status === 'pending') {
        return <Box sx={{
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        }}>
            <CircularProgress />
        </Box>
    }

    if (error) {
        return (
            <Box>
                <ErrorOutlineRoundedIcon />
                <Box>{error.message}</Box>
            </Box>
        )
    }

    const devices = (data.result && Object.keys(data.result).length > 0) ? Object.values(data.result) : []

    return (
        <Box py={2}>
            {devices.length > 0 ?
                <>
                    <FormControlWithLabelWrapper
                        label="Vyberte konkrétní zásuvku"
                        labelId="select-socket-id"
                    >
                        <Select
                            id="socket-label-id"
                            value={socketId}
                            onChange={e => {
                                const newId = e.target.value
                                setSocketId(newId)
                            }}
                        >
                            <MenuItem value={''}>
                                -
                            </MenuItem>
                            {devices.map(device => (
                                <MenuItem key={device.id} value={device.id}>
                                    {device.name}{' - '}{device.online ?
                                        <Box sx={{
                                            color: 'success.main',
                                        }} component="span">online</Box>
                                        :
                                        <Box sx={{
                                            color: 'error.main',
                                        }} component="span">offline</Box>
                                    }
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControlWithLabelWrapper>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, pt: 2 }}>
                        <CustomButton
                            text="Propojit"
                            disabled={!socketId || !!connectionSuccess}
                            onClick={() => handleConnect()}
                            loading={requestInProgress}
                        />
                        <CustomButton
                            text="Znovu načíst"
                            onClick={() => handleRefetch()}
                            variant="outlined"
                        />
                    </Box>
                    <Box pt={2}>
                        {connectionSuccess &&
                            <Alert severity="success">
                                {connectionSuccess}
                            </Alert>
                        }
                        {connectionError &&
                            <Alert severity="error">
                                {connectionError}
                            </Alert>
                        }
                    </Box>
                </>
                :
                <Box sx={{
                    py: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                    {waiting ?
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}>
                            <CircularProgress />
                            <Box>
                                Načítáme Vaše zařízení z Tuya aplikace.
                            </Box>
                        </Box>
                        :
                        data?.status === 'completed' ?
                            'Zatím nemáte žádné zásuvky' :
                            <Box>
                                <Box>Nepovedlo se spojit s Vaší Tuya aplikací, zkuste jí prosím znovu propojit:</Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                                    <CustomButton
                                        text="Znovu propojit"
                                        onClick={() => setShowGeneration(true)}
                                    />
                                </Box>
                            </Box>
                    }
                </Box>
            }
        </Box>
    )
}
